import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { focusInput, highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { IAutocompleteDataRow } from 'src/app/shared/interfaces/autocomplete-data-row.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { AddDosageForm, AddDosageUnit, GetDosageForms, GetDosageUnits, IsCompoundNdcInvalid } from 'src/app/shared/states/drugs/drugs.actions';
import { DrugsState } from 'src/app/shared/states/drugs/drugs.state';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { ICreateCompoundRequest } from '../../interfaces/create-compound-request';
import { DrugsService } from 'src/app/shared/services/drugs.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-create-compound-dialog',
  templateUrl: './create-compound-dialog.component.html',
  styleUrls: ['./create-compound-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCompoundDialogComponent implements OnInit {

  @Output() save: EventEmitter<ICreateCompoundRequest> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  @Select(DrugsState.dosageUnits) dosageUnits$: Observable<IAutocompleteDataRow[]>;
  @Select(DrugsState.dosageForms) dosageForms$: Observable<IAutocompleteDataRow[]>;

  form: FormGroup;

  @ViewChild('dialog') dialog;
  /**
   *
   */
  constructor(
    private fb: FormBuilder,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private formattedDatePipe: FormattedDatePipe,
    private drugsService: DrugsService
  ) {
    this.form = fb.group({
      name: ['', Validators.required],
      transactionDate: [this.now(), Validators.required],
      transactionDateDisplayValue: [this.formattedDatePipe.transform(this.now(), 'MMM d a, HH:mm yyyy')],
      dose: ['', Validators.required],
      dosageUnit: ['', autocompleteValidator],
      dosageForm: ['', autocompleteValidator],
      ndc: ['', [Validators.required, Validators.minLength(10)]],
      packageSize: ['', Validators.required],
    });
    
  }

  ngOnInit(): void {
    if(!this.store.selectSnapshot(DrugsState.dosageUnits)?.length) {
      this.store.dispatch(new GetDosageUnits());
    }

    if(!this.store.selectSnapshot(DrugsState.dosageForms)?.length) {
      this.store.dispatch(new GetDosageForms());
    }
  }

  submitForm(): void {
    if (this.form.invalid) {
      highlightRequiredControlsWithErrors(this.form);
      return;
    }
    const row = this.form.getRawValue();
    row.dosageUnit = row?.dosageUnit?.name;
    row.dosageForm = row?.dosageForm?.name;
    this.save.next(row);
  }

  dosageUnitAdded(val: string): void {
    this.store.dispatch(new AddDosageUnit(val));
  }

  dosageFromAdded(val: string): void {
    this.store.dispatch(new AddDosageForm(val));
  }

  handleLastInputKeyEnterTab(): void {
    event.preventDefault();
    if (this.form.valid) {
      setTimeout(() => {
        this.dialog?.focusSubmitButton();
      });
      return;
    }
    setTimeout(() => {
      this.dialog.focusCancelButton();
    });
  }

  focusInput(viewChild): void {
    focusInput(viewChild)
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }

  ndcInputBlurred(): void {
    const ndcControl = this.form.get('ndc');
    if (ndcControl.value?.length >= 10) {
      this.store.dispatch(new IsCompoundNdcInvalid(ndcControl.value))
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(x => {
        if (this.store.selectSnapshot(DrugsState.isCompoundNdcInvalid)) {
          ndcControl.setErrors({duplicateNdc: true});
          this.cdr.markForCheck();
        }
      })
    }
  }


  now(): Date {
    return new Date();
  }

}
